import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { parseSubjectDN, maskEmail } from '../../core/utils/cert-subject.utils';
import { ResetPinOtpService } from '../../core/services/reset-pin-otp.service';
import { UiHelperService } from '../../services/ui-helper.service';

export interface TokenFisicoOtpChannelModalData {
  /** SubjectDN del certificado para extraer E= */
  subjectDN?: string;
  /** Opcional: correo ya extraído (si no se pasa, se extrae de subjectDN) */
  email?: string;
}

export interface TokenFisicoOtpChannelModalResult {
  channel: 'email';
  /** Correo completo (para mostrar en modal de validación) */
  email: string;
  sendOtpRequested: true;
  /** transactionId de la respuesta de generate OTP (email); el modal cierra con esto tras enviar con éxito */
  transactionId: string;
}

@Component({
  selector: 'app-token-fisico-otp-channel-modal',
  templateUrl: './token-fisico-otp-channel-modal.component.html',
  styleUrls: ['./token-fisico-otp-channel-modal.component.css'],
  standalone: false
})
export class TokenFisicoOtpChannelModalComponent implements OnInit, OnChanges {
  /** Correo extraído (completo, para enviar OTP) */
  email: string | null = null;
  /** Correo enmascarado para mostrar */
  maskedEmail = '';
  /** Enviando OTP por correo (spinner en botón) */
  sendingOtpEmail = false;
  /** Error al enviar OTP */
  sendOtpError: string | null = null;

  @Input() embedded = false;
  @Input() embeddedData: TokenFisicoOtpChannelModalData | null = null;

  @Output() embeddedCancel = new EventEmitter<void>();
  @Output() embeddedResult = new EventEmitter<TokenFisicoOtpChannelModalResult>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<TokenFisicoOtpChannelModalComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public injectedData: TokenFisicoOtpChannelModalData | null,
    private resetPinOtpService: ResetPinOtpService,
    private uiHelper: UiHelperService
  ) {}

  ngOnInit(): void {
    this.applyData(this.embeddedData ?? this.injectedData);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embeddedData'] && this.embeddedData) {
      this.applyData(this.embeddedData);
    }
  }

  private applyData(data: TokenFisicoOtpChannelModalData | null | undefined): void {
    if (!data) return;
    const parsed = data.subjectDN ? parseSubjectDN(data.subjectDN) : {};
    this.email = data.email ?? parsed.email ?? null;
    if (this.email) this.maskedEmail = maskEmail(this.email, 6);
  }

  get canSendOtpEmail(): boolean {
    return !!this.email;
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.embeddedCancel.emit();
    }
  }

  sendOtpEmail(): void {
    if (!this.canSendOtpEmail || this.sendingOtpEmail) return;
    this.sendOtpError = null;
    this.sendingOtpEmail = true;
    this.uiHelper.showSpinner('Enviando código de verificación…', 'admin');
    this.resetPinOtpService.generateOtpEmail(this.email!).subscribe({
      next: (res) => {
        this.sendingOtpEmail = false;
        this.uiHelper.hideSpinner();
        if (res?.transactionId) {
          const payload: TokenFisicoOtpChannelModalResult = {
            channel: 'email',
            email: this.email!,
            sendOtpRequested: true,
            transactionId: res.transactionId
          };
          if (this.dialogRef) {
            this.dialogRef.close(payload);
          } else {
            this.embeddedResult.emit(payload);
          }
        } else {
          this.sendOtpError = 'No se recibió identificador de transacción. Intente de nuevo.';
        }
      },
      error: (err) => {
        this.sendingOtpEmail = false;
        this.uiHelper.hideSpinner();
        this.sendOtpError = err?.message || 'No se pudo enviar el código al correo.';
      }
    });
  }
}
